/**
 * Contacts API service - handles organization contact directory API calls
 */

import { apiClient } from './client';

// Types
export interface OrganizationContact {
  id: string;
  organizationId: string;
  email?: string | undefined;
  name?: string | undefined;
  company?: string | undefined;
  phone?: string | undefined;
  userId?: string | undefined;
  createdAt: string;
  updatedAt: string;
}

export interface ContactListResponse {
  items: OrganizationContact[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface CreateContactRequest {
  organizationId: string;
  email?: string | undefined;
  name?: string | undefined;
  company?: string | undefined;
  phone?: string | undefined;
  userId?: string | undefined;
}

export interface UpdateContactRequest {
  email?: string | undefined;
  name?: string | undefined;
  company?: string | undefined;
  phone?: string | undefined;
  userId?: string | undefined;
}

export interface ContactSearchParams {
  organizationId: string;
  search?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

// Bulk import types
export interface ContactImportItem {
  email?: string;
  name?: string;
  company?: string;
  phone?: string;
}

export interface ContactBulkImportRequest {
  organizationId: string;
  contacts: ContactImportItem[];
  skipDuplicates?: boolean;
}

export interface ContactImportResult {
  email?: string | null;
  name?: string | null;
  success: boolean;
  error?: string | null;
  contactId?: string | null;
}

export interface ContactBulkImportResponse {
  total: number;
  imported: number;
  skipped: number;
  failed: number;
  results: ContactImportResult[];
}

// Transform backend response to frontend format
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformContact(data: any): OrganizationContact {
  return {
    id: data.id,
    organizationId: data.organizationId ?? data.organization_id,
    email: data.email ?? undefined,
    name: data.name ?? undefined,
    company: data.company ?? undefined,
    phone: data.phone ?? undefined,
    userId: data.userId ?? data.user_id ?? undefined,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

/**
 * Contacts API methods
 */
export const contactsApi = {
  /**
   * Search/list contacts for an organization
   */
  async list(params: ContactSearchParams): Promise<ContactListResponse> {
    const queryParams = new URLSearchParams({
      organization_id: params.organizationId,
    });

    if (params.search) {
      queryParams.set('search', params.search);
    }
    if (params.page) {
      queryParams.set('page', params.page.toString());
    }
    if (params.pageSize) {
      queryParams.set('page_size', params.pageSize.toString());
    }

    const response = await apiClient.get<any>(
      `/contacts?${queryParams.toString()}`
    );
    const data = response.data;

    return {
      items: (data.items ?? []).map(transformContact),
      total: data.total,
      page: data.page,
      pageSize: data.pageSize ?? data.page_size,
      hasMore: data.hasMore ?? data.has_more,
    };
  },

  /**
   * Get a single contact by ID
   */
  async get(contactId: string): Promise<OrganizationContact> {
    const response = await apiClient.get<any>(
      `/contacts/${contactId}`
    );
    return transformContact(response.data);
  },

  /**
   * Create a new contact
   */
  async create(data: CreateContactRequest): Promise<OrganizationContact> {
    const response = await apiClient.post<any>('/contacts', {
      organization_id: data.organizationId,
      email: data.email,
      name: data.name,
      company: data.company,
      phone: data.phone,
      user_id: data.userId,
    });
    return transformContact(response.data);
  },

  /**
   * Update a contact
   */
  async update(
    contactId: string,
    data: UpdateContactRequest
  ): Promise<OrganizationContact> {
    const response = await apiClient.patch<any>(
      `/contacts/${contactId}`,
      {
        email: data.email,
        name: data.name,
        company: data.company,
        phone: data.phone,
        user_id: data.userId,
      }
    );
    return transformContact(response.data);
  },

  /**
   * Delete a contact
   */
  async delete(contactId: string): Promise<void> {
    await apiClient.delete(`/contacts/${contactId}`);
  },

  /**
   * Bulk import contacts from a list (e.g., parsed from CSV)
   */
  async bulkImport(
    data: ContactBulkImportRequest
  ): Promise<ContactBulkImportResponse> {
    const response = await apiClient.post<any>('/contacts/import', {
      organization_id: data.organizationId,
      contacts: data.contacts,
      skip_duplicates: data.skipDuplicates ?? true,
    });

    const respData = response.data;
    return {
      total: respData.total,
      imported: respData.imported,
      skipped: respData.skipped,
      failed: respData.failed,
      results: (respData.results ?? []).map((r: any) => ({
        email: r.email ?? null,
        name: r.name ?? null,
        success: r.success,
        error: r.error ?? null,
        contactId: r.contactId ?? r.contact_id ?? null,
      })),
    };
  },
};
